<script setup>
import { computed, ref, onMounted, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useResizeObserver, useScroll } from '@vueuse/core';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  fullWidth: {
    type: Boolean,
    default: false,
  },
  initialActiveTab: {
    type: Number,
    default: 0,
  },
  tabs: {
    type: Array,
    required: true,
    validator: value => {
      return value.every(
        tab =>
          typeof tab.label === 'string' &&
          (tab.count ? typeof tab.count === 'number' : true)
      );
    },
  },
});

const emit = defineEmits(['tabChanged']);
const { t } = useI18n();

const activeTab = computed(() => props.initialActiveTab);

const tabRefs = ref([]);
const indicatorStyle = ref({});
const enableTransition = ref(false);

const activeElement = computed(() => tabRefs.value[activeTab.value]);

const scrollContainer = ref(null);
const { arrivedState } = useScroll(scrollContainer);

const canScrollLeft = computed(() => !arrivedState.left);
const canScrollRight = computed(() => !arrivedState.right);

const scrollLeft = () => {
  if (scrollContainer.value) {
    scrollContainer.value.scrollBy({ left: -200, behavior: 'smooth' });
  }
};

const scrollRight = () => {
  if (scrollContainer.value) {
    scrollContainer.value.scrollBy({ left: 200, behavior: 'smooth' });
  }
};

const updateIndicator = () => {
  nextTick(() => {
    if (!activeElement.value) return;

    indicatorStyle.value = {
      left: `${activeElement.value.offsetLeft}px`,
      width: `${activeElement.value.offsetWidth}px`,
    };

    if (scrollContainer.value) {
      const containerLeft = scrollContainer.value.scrollLeft;
      const containerRight = containerLeft + scrollContainer.value.clientWidth;
      const tabLeft = activeElement.value.offsetLeft;
      const tabRight = tabLeft + activeElement.value.offsetWidth;

      if (tabLeft < containerLeft || tabRight > containerRight) {
        scrollContainer.value.scrollTo({
          left:
            tabLeft -
            scrollContainer.value.clientWidth / 2 +
            activeElement.value.offsetWidth / 2,
          behavior: 'smooth',
        });
      }
    }
  });
};

useResizeObserver(activeElement, updateIndicator);

// Watch for prop/tabs changes to update indicator position
watch([() => props.initialActiveTab, () => props.tabs], updateIndicator, {
  immediate: true,
});

onMounted(() => {
  nextTick(() => {
    enableTransition.value = true;
    setTimeout(() => updateIndicator(), 100);
  });
});

const selectTab = index => {
  emit('tabChanged', props.tabs[index]);
};

const showDivider = index => {
  return (
    // Show dividers after the active tab, but not after the last tab
    (index > activeTab.value && index < props.tabs.length - 1) ||
    // Show dividers before the active tab, but not immediately before it and not before the first tab
    (index < activeTab.value - 1 && index > -1)
  );
};
</script>

<template>
  <div
    class="relative flex items-center w-full max-w-full group"
    :class="fullWidth ? 'px-8' : ''"
  >
    <!-- Left Arrow -->
    <Button
      v-if="canScrollLeft"
      icon="i-lucide-chevron-left"
      :aria-label="t('PAGINATION_FOOTER.PREVIOUS_PAGE')"
      variant="ghost"
      color="slate"
      size="sm"
      class="!absolute !left-1 !z-20 !h-7 !w-7 !rounded-md !border !border-n-strong !bg-n-solid-2 !shadow-sm dark:!bg-n-solid-3"
      @click="scrollLeft"
    />

    <div
      ref="scrollContainer"
      class="relative flex items-center h-8 rounded-lg bg-n-alpha-1 dark:bg-n-solid-1 max-w-full overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] transition-all duration-200 ease-out has-[button:active]:scale-[1.01]"
      :class="[fullWidth ? 'w-full' : 'w-fit']"
      role="tablist"
    >
      <div
        class="absolute rounded-lg bg-n-solid-active shadow-sm pointer-events-none h-8 outline-1 outline outline-n-container inset-y-0"
        :class="{ 'transition-all duration-300 ease-out': enableTransition }"
        :style="indicatorStyle"
      />

      <template v-for="(tab, index) in tabs" :key="index">
        <button
          :ref="el => (tabRefs[index] = el)"
          type="button"
          role="tab"
          :aria-selected="activeTab === index"
          class="relative z-10 px-4 whitespace-nowrap py-1.5 text-sm border-0 outline-1 outline-transparent rounded-lg transition-all duration-200 ease-out hover:text-n-brand active:scale-[1.02]"
          :class="[
            activeTab === index
              ? 'text-n-blue-11 scale-100'
              : 'text-n-slate-10 scale-[0.98]',
            fullWidth ? 'flex-1' : '',
          ]"
          @click="selectTab(index)"
        >
          {{ tab.label }} {{ tab.count ? `(${tab.count})` : '' }}
        </button>
        <div
          v-if="index < tabs.length - 1"
          class="w-px h-3.5 rounded my-auto transition-colors duration-300 ease-in-out shrink-0"
          :class="
            showDivider(index)
              ? 'bg-n-strong'
              : 'bg-transparent dark:bg-transparent'
          "
        />
      </template>
    </div>

    <!-- Right Arrow -->
    <Button
      v-if="canScrollRight"
      icon="i-lucide-chevron-right"
      :aria-label="t('PAGINATION_FOOTER.NEXT_PAGE')"
      variant="ghost"
      color="slate"
      size="sm"
      class="!absolute !right-1 !z-20 !h-7 !w-7 !rounded-md !border !border-n-strong !bg-n-solid-2 !shadow-sm dark:!bg-n-solid-3"
      @click="scrollRight"
    />
  </div>
</template>
